import { Selection, ScaleLinear, scaleLinear, ScaleTime, EnterElement, extent } from "d3"
import { ArrowUpdate, D3ValueAxis, D3ValueAxisConfig } from "../../../D3/D3ValueAxis"
import { D3TracesWrapper } from "./D3TracesWrapper"
import { D3ModalityGraphLegend } from "./D3ModalityGraphLegend"
import { D3SummaryValues } from "../../../D3/D3SummaryValues"
import { D3HorizontalLines } from "../../../D3/D3HorizontalLines"
import { D3VerticalLines } from "../../../D3/D3VerticalLines"
import { ModalityGraphGroupReactCallbacks, } from "../../../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { ModalityGraphConfig, ModalityGraphConfigWithDerivedTraces } from "../../../../Types/Graph"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { DeltaRegionJSON, getFullTraceDataConfigs, LineTraceConfigJSON } from "../../../../Types/Trace"
import { getSignal } from "../../../../Data/TimeSeriesData"
import { sortedIndexBy } from "lodash"
import { MULTI_GRAPH_SPACING, NUMERIC_VALUE_MARGIN } from "../../Constants"

export class D3ModalityGraph extends D3OneToOneRenderable<SVGGElement, SVGGElement, ModalityGraphConfig, ModalityGraphGroupReactCallbacks> {
	private yScale: ScaleLinear<any, any, any>
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private graph: ModalityGraphConfigWithDerivedTraces 
	private backgroundClassName = "d3-modality-graph-background"
	private middlegroundClassName = "d3-modality-graph-middleground"
	private foregroundClassName = "d3-modality-graph-foreground"

	// Children
	private yAxis?: D3ValueAxis
	private tracesWrapper?: D3TracesWrapper
	private legend?: D3ModalityGraphLegend
	private summaryValues?: D3SummaryValues
	private horizontalLines?: D3HorizontalLines
	private verticalLines?: D3VerticalLines
	private viewScale: ScaleTime<any, any, any>

	constructor(root: SVGGElement, config: ModalityGraphConfig, pageManager: TimeSeriesPageManager<ModalityPage>, reactCallbacks: ModalityGraphGroupReactCallbacks, viewScale: ScaleTime<any, any, any>) {
		super(root, config, "d3-modality-graph", reactCallbacks)
		this.pageManager = pageManager
		this.viewScale = viewScale

		const { min, max, height } = this.config
		
		this.yScale = scaleLinear()
			.domain([min, max])
			.range([height ?? 0, 0])

		this.graph = {} as ModalityGraphConfigWithDerivedTraces

		this.updateDerivedState()
		this.render()
	}

	protected updateDerivedState(): void {
		this.graph = this.getDerivedGraph() as any
		this.updateChildren()
	}

	public renderPage = (page: ModalityPage) => {
		this.tracesWrapper?.renderPage(page)
		this.summaryValues?.render()
	}

	public viewTimesChanged = () => {
		this.verticalLines?.render()
		this.tracesWrapper?.render()
		this.summaryValues?.render()
		this.yAxis?.updateArrows(this.getDataOutOfViewConfigs())
	}

	public rescale = () => {
		this.horizontalLines?.render()
		this.verticalLines?.render()
		this.tracesWrapper?.rescale()
	}

	public takeSnapshot = () => {
		this.tracesWrapper?.takeSnapshot()
	}

	public clearSnapshot = () => {
		this.tracesWrapper?.clearSnapshot()
	}

	public isRescaling = (): boolean => {
		return (this.yAxis?.isDragging ?? false) || (this.tracesWrapper?.isRescaling() ?? false)
	}

	public getDerivedGraph = () => {
		const { height } = this.config
		this.yScale.range([height ?? 0, 0])
		return {...this.config, traces: this.config.traces.map(traceConfig => ({ ...traceConfig, id: traceConfig.id ?? new Date(Date.now()).toISOString(), graphId: this.config.id, xScale: this.config.xScale, yScale: this.yScale }))}
	}

	protected enter = (newGraph: Selection<EnterElement, any, any, any>): Selection<SVGGElement, any, any, any> => {
		const graphGroup = newGraph
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(0, ${this.config.offset})`)

		// When individual children update, they sometimes change order, but they are stacked on top of each other.
		// These containers create a consistent z-index order so that some elements to not get hidden behind others.
		const background = graphGroup.append("g")
			.attr("class", this.backgroundClassName)

		const middleground = graphGroup.append("g")
			.attr("class", this.middlegroundClassName)

		const foreground = graphGroup.append("g")
			.attr("class", this.foregroundClassName)
		
		background.each((config, i, nodes) => this.createBackgroundChildren(config, i, nodes))
		middleground.each((config, i, nodes) => this.createMiddlegroundChildren(config, i, nodes))
		foreground.each((config, i, nodes) => this.createForegroundChildren(config, i, nodes))
		return graphGroup
	}

	protected update = (updatedGraphs: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		updatedGraphs.attr("transform", `translate(0, ${this.config.offset})`)
		this.renderChildren()
		return updatedGraphs
	}

	private createBackgroundChildren = (config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.verticalLines = new D3VerticalLines(root, { xScale: this.config.xScale, height: this.config.height })
	}

	private createMiddlegroundChildren = (config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.tracesWrapper = new D3TracesWrapper(root, { traces: this.graph.traces }, this.pageManager, this.reactCallbacks)
	}

	private createForegroundChildren = (config: ModalityGraphConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.yAxis = new D3ValueAxis(root, this.getAxisConfig(), this.reactCallbacks)
		this.horizontalLines = new D3HorizontalLines(root, { yScale: this.yScale, width: this.config.width })
		this.legend = new D3ModalityGraphLegend(root, { 
			traces: this.graph.traces as LineTraceConfigJSON[], 
			width: this.config.width, 
			graphId: this.config.id,
		}, this.reactCallbacks)

		const summaryValueConfigs = this.config.traces.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible)
		const summaryValueColumns = this.config.summaryValueColumns ?? 1

		this.summaryValues = new D3SummaryValues(root, {
			traces: summaryValueConfigs as (LineTraceConfigJSON | DeltaRegionJSON)[],
			height: this.config.height + MULTI_GRAPH_SPACING, 
			x: this.config.width,
			y: -MULTI_GRAPH_SPACING/2,
			width: NUMERIC_VALUE_MARGIN, 
			rows: Math.ceil(summaryValueConfigs.length / summaryValueColumns), 
			columns: summaryValueColumns
		}, "d3-modality-graph-summary-values", this.reactCallbacks, this.pageManager, this.viewScale)
	}

	protected updateChildren = () => {
		this.yAxis?.updateConfig(this.getAxisConfig())
		this.tracesWrapper?.updateConfig({ traces: this.graph.traces })
		this.legend?.updateConfig({ traces: this.graph.traces as LineTraceConfigJSON[], width: this.config.width, graphId: this.config.id })

		const summaryValueConfigs = this.config.traces.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible)
		const summaryValueColumns = this.config.summaryValueColumns ?? 1

		this.summaryValues?.updateConfig({
			traces: summaryValueConfigs as (LineTraceConfigJSON | DeltaRegionJSON)[],
			height: this.config.height + MULTI_GRAPH_SPACING, 
			x: this.config.width,
			y: -MULTI_GRAPH_SPACING/2,
			width: NUMERIC_VALUE_MARGIN, 
			rows: Math.ceil(summaryValueConfigs.length / summaryValueColumns), 
			columns: summaryValueColumns
		})
		this.horizontalLines?.updateConfig({yScale: this.yScale, width: this.config.width})
		this.verticalLines?.updateConfig({xScale: this.config.xScale, height: this.config.height})
	}

	private renderChildren = () => {
		this.yAxis?.render()
		this.tracesWrapper?.render()
		this.legend?.render()
		this.summaryValues?.render()
		this.horizontalLines?.render()
		this.verticalLines?.render()
	}

	private onValueAxisDrag = () => {
		this.horizontalLines?.render()
		this.verticalLines?.render()
		this.tracesWrapper?.redraw()
		this.yAxis?.updateArrows(this.getDataOutOfViewConfigs())
	}

	private onValueAxisDragEnd = () => {
		const [min, max] = this.yScale.domain()
		this.yAxis?.updateArrows(this.getDataOutOfViewConfigs())
		this.updateGraphYBounds(min, max)
	}

	private getAxisConfig = (): D3ValueAxisConfig => {
		const { downArrow, upArrow } = this.getDataOutOfViewConfigs()

		return {
			scale: this.yScale, 
			graphId: this.config.id, 
			upArrow,
			downArrow,
			onDrag: this.onValueAxisDrag,
			onDoubleClick: this.onYAxisDoubleClick,
			onDragEnd: this.onValueAxisDragEnd
		}
	}

	public autoScale = () => {
		let min: number | undefined
		let max: number | undefined

		if (this.config.autoScale) {
			const allExtentValues: number[] = []
			const traces = this.graph.traces

			this.pageManager.getPagesInView().forEach(page => {
				const extentPerTrace = traces
					.filter(trace => trace.isVisible ?? true)
					.flatMap(getFullTraceDataConfigs)
					.flatMap(trace => {
						const dataObjectId = this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity
						const traceData = page?.data.get(dataObjectId)?.get(trace.dataKey)
						
						if (!traceData) {
							return []
						}

						const startIndex = sortedIndexBy(traceData.times, this.config.xScale.domain()[0].getTime())
						const endIndex = sortedIndexBy(traceData.times, this.config.xScale.domain()[1].getTime())
						const signal = getSignal(traceData, trace)
						const relevantData = signal.slice(startIndex, endIndex)

						return extent(relevantData)
					})
					.filter(p => p !== undefined) as number[]

				allExtentValues.push(...extentPerTrace)
			})

			const extentOfValues = extent(allExtentValues)

			min = extentOfValues[0]
			max = extentOfValues[1]

			// explicitly check for undefined because 0 would return false
			if (min !== undefined && max !== undefined) {

				if (min === max) {
					min -= 1
				}

				// Bump the values by 1% to make sure that values exactly on the bounds are still visible.
				min -= 0.01 * (max - min)
				max += 0.01 * (max - min)
			}
		}

		if (min !== undefined && max !== undefined) {
			this.updateGraphYBounds(min, max)
		}
	}

	private onYAxisDoubleClick = () => {
		if (this.config.autoScale) {
			this.autoScale()
		} else {
			this.updateGraphYBounds(this.config.defaultMin, this.config.defaultMax)
		}
	}

	private updateGraphYBounds = (min: number, max: number) => {
		const [oldMin, oldMax] = this.yScale.domain()

		if (min === oldMin && max === oldMax) {
			return // don't bother.
		}

		this.yScale.domain([min, max])
		this.tracesWrapper?.redraw()
		this.yAxis?.render()
	}

	private getDataOutOfViewConfigs(): ArrowUpdate {
		// Find if any data is above or below the current y-axis bounds
		const [startDate, endDate] = this.viewScale.domain()
		const startTime = startDate.getTime()
		const endTime = endDate.getTime()
 		const [yMin, yMax] = this.yScale.domain()
		const pagesInView = this.pageManager.getPagesInView()
		const allTraceDataConfigs = this.config.traces.filter(trace => (trace.isVisible ?? true)).flatMap(getFullTraceDataConfigs)

		let minValue = Infinity
		let maxValue = -Infinity
		let downArrow = undefined
		let upArrow = undefined

		for (const trace of allTraceDataConfigs) {
			const dataObjectId = this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity
			for (const page of pagesInView) {
				const traceData = page?.data.get(dataObjectId)?.get(trace.dataKey)

				if (!traceData) {
					continue
				}

				const startIndex = sortedIndexBy(traceData.times, startTime)
				const endIndex = sortedIndexBy(traceData.times, endTime)
				const relevantData = getSignal(traceData, trace).slice(startIndex, endIndex)

				if (relevantData.length === 0) {
					continue
				}

				minValue = Math.min(...relevantData, minValue)
				maxValue = Math.max(...relevantData, maxValue)
			}

			if (minValue < yMin) {
				downArrow = { color: "var(--color-chart-arrow)" }
			}

			if (maxValue > yMax) {
				upArrow = { color: "var(--color-chart-arrow)" }
			}

			if (downArrow && upArrow) {
				// We found two arrows already, no need to look for any more
				break
			}
		}

		return { upArrow, downArrow }
	}
}
